import { useSelector, useDispatch } from 'react-redux';
import { Box, Flex } from '@chakra-ui/react';
import { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';

import {
  createInstrument,
  getCalculationLoadingStatus,
  getEditInstrumentData,
  getSelectedDashboardScenario,
  getCompareDashboardScenario,
  getSelectedRow,
  setEditInstrumentData,
  setEditInstrumentOutput,
  getDashboardBlocks,
} from 'redux/DashboardSliceV2';
import { AppDispatch } from 'utils/GlobalHelpers';
import { STATUS } from 'projectConstants';

import DashboardModalFilters from '../../Filters';
import useInstrumentDates from '../../hooks/useInstrumentDates';

import ChartType from './ChartType';
import EditTitle from './EditTitle';
import ModalBlock from './ModalBlock';
import ModalIndicator from './ModalIndicator';
import RowFormatting from './Formatting/RowFormatting';
import RowIndentation from './Formatting/RowIndentation';
import RowCellColor from './Formatting/RowCellColor';
import RowTextColor from './Formatting/RowTextColor';
import RowSort from './Formatting/RowSort';
import ColumnSettingsPopover from './ColumnSettingPopover';
import IndicatorSelector from './IndicatorSelector';
import ReportingWindowOverride from './ReportingWindowOverride';

const Setup = () => {
  const calculationsLoading = useSelector(getCalculationLoadingStatus);
  const isLoading = !(
    calculationsLoading === STATUS.SUCCESS || calculationsLoading === STATUS.IDLE
  );
  const dispatch: AppDispatch = useDispatch();
  const widget = useSelector(getEditInstrumentData);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const currentSelectedRow = useSelector(getSelectedRow);
  const blockDetails = useSelector(getDashboardBlocks);
  const { buildPreviewPayload } = useInstrumentDates();
  const [selectedRow, setSelectedRow] = useState<any>(currentSelectedRow);

  // const handleSettingsChange = (updatedSettings: any) => {
  //   console.log('updatedSettings', updatedSettings);
  //   dispatch(
  //     setEditInstrumentData({
  //       ...widget,
  //       formatting: {
  //         ...widget?.formatting,
  //         columns_settings: {
  //           ...updatedSettings,
  //         },
  //       },
  //     }),
  //   );
  //   const reportingPeriod = dashboardReportingPeriod
  //     ? {
  //         preset: dashboardReportingPeriod.preset || 'Custom',
  //         start: dashboardReportingPeriod.start || '',
  //         end: dashboardReportingPeriod.end || '',
  //       }
  //     : null;
  //   const compareScenarioId = compareDashboardScenario?.id || null;
  //   dispatch(
  //     createInstrument({
  //       request_body: {
  //         ...widget,
  //         scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
  //         formatting: {
  //           ...widget?.formatting,
  //           columns_settings: {
  //             ...widget?.formatting?.columns_settings,
  //             ...updatedSettings,
  //           },
  //         },
  //         save: false,
  //         data_values: true,
  //         start_date: timeFilter?.start || '',
  //         end_date: timeFilter?.end || '',
  //         reporting_period: reportingPeriod,
  //         compare_scenario: compareScenarioId,
  //       },
  //     }),
  //   ).then((result) => {
  //     dispatch(setEditInstrumentOutput(result?.payload?.data));
  //   });
  // };

  const findIndicatorIndex = (indicators: any, indicatorId: string) => {
    return widget?.instrument_indicators.findIndex(
      (indicator: any) => indicator.indicator_id === indicatorId,
    );
  };

  useEffect(() => {
    if (currentSelectedRow) {
      const index = findIndicatorIndex(
        widget.instrument_indicators,
        currentSelectedRow?.indicator_id,
      );
      setSelectedRow(widget.instrument_indicators[index]);
    }
  }, [widget, currentSelectedRow]);

  const setChartType = (type: string) => {
    dispatch(setEditInstrumentData({ ...widget, instrument_type: type }));
  };

  const setTitle = (title: string) => {
    dispatch(setEditInstrumentData({ ...widget, name: title }));
  };

  const handleKPIBlockChange = (block: any) => {
    dispatch(
      setEditInstrumentData({
        ...widget,
        instrument_indicators: [
          {
            block_id: block.block_id,
            indicator_id: null,
            aggregate_function: 'sum',
            indicator_name: '', // TODO: unused should be removed
            formatting: {}, // TODO: unused should be removed
          },
        ],
      }),
    );
  };

  const handleKPIIndicatorChange = (indicator: any) => {
    const updatedIndicators = [
      {
        ...widget.instrument_indicators[0],
        indicator_id: indicator.indicator_id,
        indicator_name: indicator.indicator_name,
        formatting: {},
      },
    ];
    const updatedWidget = { ...widget, instrument_indicators: updatedIndicators };
    dispatch(setEditInstrumentData(updatedWidget));
    dispatch(
      createInstrument({
        request_body: buildPreviewPayload(updatedWidget, {
          scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
          compare_scenario: compareDashboardScenario?.id || null,
        }),
      }),
    ).then((result) => dispatch(setEditInstrumentOutput(result?.payload?.data)));
  };

  const fetchOutput = (newWidget: any, dataValues: boolean = true) => {
    dispatch(setEditInstrumentData(newWidget));
    dispatch(
      createInstrument({
        request_body: buildPreviewPayload(newWidget, {
          scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
          compare_scenario: compareDashboardScenario?.id || null,
          data_values: dataValues,
        }),
      }),
    ).then((result) => {
      if (dataValues === true) dispatch(setEditInstrumentOutput(result?.payload?.data));
    });
  };

  /** Handles updates to the instrument's formatting object (e.g. window override). */
  const handleFormattingChange = (updatedFormatting: Record<string, any>) => {
    const newWidget = { ...widget, formatting: { ...widget?.formatting, ...updatedFormatting } };
    fetchOutput(newWidget);
  };

  const handleTextDecoration = debounce((content: any) => {
    if (widget && selectedRow) {
      const index = findIndicatorIndex(widget.instrument_indicators, selectedRow.indicator_id);
      const newWidget = JSON.parse(JSON.stringify({ ...widget }));
      newWidget.instrument_indicators[index].formatting = {
        ...newWidget.instrument_indicators[index].formatting,
        textDecoration: content,
      };
      fetchOutput(newWidget, widget?.instrument_type === 'table');
    }
  }, 1000);

  const handleColorCellChange = debounce((content: any) => {
    if (widget && selectedRow) {
      const index = findIndicatorIndex(widget.instrument_indicators, selectedRow.indicator_id);
      const newWidget = JSON.parse(JSON.stringify({ ...widget }));
      newWidget.instrument_indicators[index].formatting = {
        ...newWidget.instrument_indicators[index].formatting,
        cellColor: content,
      };
      fetchOutput(newWidget, widget?.instrument_type === 'table');
    }
  }, 1000);

  const handleColorChange = debounce((content: any) => {
    if (widget && selectedRow) {
      const index = findIndicatorIndex(widget.instrument_indicators, selectedRow.indicator_id);
      const newWidget = JSON.parse(JSON.stringify({ ...widget }));
      newWidget.instrument_indicators[index].formatting = {
        ...newWidget.instrument_indicators[index].formatting,
        textColor: content,
      };
      fetchOutput(newWidget, widget?.instrument_type === 'table');
    }
  }, 1000);

  const handleAlignemntChange = debounce((content: any) => {
    if (widget && selectedRow) {
      const index = findIndicatorIndex(widget.instrument_indicators, selectedRow.indicator_id);
      const newWidget = JSON.parse(JSON.stringify({ ...widget }));
      newWidget.instrument_indicators[index].formatting = {
        ...newWidget.instrument_indicators[index].formatting,
        textAlign: content,
      };
      fetchOutput(newWidget, widget?.instrument_type === 'table');
    }
  }, 1000);

  /**
   * True when the currently selected indicator uses percentage formatting.
   * Variance-percentage sort options are meaningless for percentage indicators
   * (the backend suppresses % variance columns for them) so we disable those options.
   */
  const isPercentageIndicator = useMemo(() => {
    if (!selectedRow || !Array.isArray(blockDetails)) return false;
    const block = blockDetails.find((b: any) => b.block_id === selectedRow.block_id);
    const indicator = block?.indicators?.find(
      (ind: any) => ind.indicator_id === selectedRow.indicator_id,
    );
    return indicator?.data_format === 'Percentage';
  }, [selectedRow, blockDetails]);

  /**
   * Saves a fully-resolved row_sort config onto the selected indicator and
   * triggers a single backend recalculation. Only called when the user clicks
   * Apply inside the RowSort popover, so there is no debounce needed.
   */
  const handleRowSortChange = (rowSort: { enabled: boolean; key: string; direction: string }) => {
    if (widget && selectedRow) {
      const index = findIndicatorIndex(widget.instrument_indicators, selectedRow.indicator_id);
      const newWidget = JSON.parse(JSON.stringify({ ...widget }));
      newWidget.instrument_indicators[index].formatting = {
        ...newWidget.instrument_indicators[index].formatting,
        row_sort: rowSort,
      };
      fetchOutput(newWidget);
    }
  };

  const blockId =
    widget?.instrument_indicators && widget?.instrument_indicators?.length > 0
      ? widget?.instrument_indicators[0].block_id
      : null;

  const indicatorId =
    widget?.instrument_indicators && widget?.instrument_indicators?.length > 0
      ? widget?.instrument_indicators[0].indicator_id
      : null;

  const isTableOrVariance =
    widget?.instrument_type === 'table' || widget?.instrument_type === 'variance';
  const CARD_STYLE = {
    bg: 'white',
    border: '1px solid #D9D9D9',
    borderRadius: '11px',
    p: '11px',
  };

  return (
    <Flex
      py='11px'
      px='18px'
      gap='16px'
      bg='white'
      flexDirection={'column'}
      style={{
        opacity: isLoading ? 0.5 : 1,
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      {!isTableOrVariance && <EditTitle title={widget?.name} setTitle={setTitle} />}
      {(widget?.instrument_type === 'line' ||
        widget?.instrument_type === 'bar' ||
        widget?.instrument_type === 'stack') && (
        <ChartType chartType={widget?.instrument_type || 'line'} setChartType={setChartType} />
      )}
      {widget?.instrument_type === 'kpi' && (
        <>
          <ModalBlock blockId={blockId} handleBlockChange={handleKPIBlockChange} />
          <ModalIndicator
            blockId={blockId}
            indicatorId={indicatorId}
            handleIndicatorChange={handleKPIIndicatorChange}
          />
          {/* KPI: reporting window override sits right after the indicator selector */}
          <ReportingWindowOverride
            formatting={widget?.formatting || {}}
            onChange={handleFormattingChange}
          />
        </>
      )}
      {/* Table / Variance: Title, Rows, Columns, and Time window grouped into one card, per Figma */}
      {isTableOrVariance && (
        <Flex {...CARD_STYLE} flexDirection='column' gap='18px'>
          <EditTitle title={widget?.name} setTitle={setTitle} />
          <IndicatorSelector />
          {widget?.instrument_type === 'variance' && (
            <ColumnSettingsPopover
              settings={widget?.formatting?.columns_settings}
              // onSettingsChange={handleSettingsChange}
            />
          )}
          <ReportingWindowOverride
            formatting={widget?.formatting || {}}
            onChange={handleFormattingChange}
          />
        </Flex>
      )}
      {(widget?.instrument_type === 'line' ||
        widget?.instrument_type === 'bar' ||
        widget?.instrument_type === 'stack') && (
        <>
          <IndicatorSelector />
          <ReportingWindowOverride
            formatting={widget?.formatting || {}}
            onChange={handleFormattingChange}
          />
        </>
      )}
      {(widget?.instrument_type === 'line' ||
        widget?.instrument_type === 'bar' ||
        widget?.instrument_type === 'stack') && <DashboardModalFilters widget={widget} />}
      {isTableOrVariance && (
        <Box {...CARD_STYLE}>
          <DashboardModalFilters widget={widget} />
        </Box>
      )}
      {((widget?.instrument_type === 'table' && selectedRow) ||
        widget?.instrument_type === 'variance') && (
        <>
          <Box {...CARD_STYLE}>
            <RowFormatting
              handleTextDecoration={handleTextDecoration}
              selectedStyle={selectedRow?.formatting?.textDecoration || ''}
            />
          </Box>
          <Box {...CARD_STYLE}>
            <RowCellColor
              selectedCellColor={selectedRow?.formatting?.cellColor || ''}
              handleCellColorChange={handleColorCellChange}
            />
          </Box>
          <Box {...CARD_STYLE}>
            <RowTextColor
              selectedColor={selectedRow?.formatting?.textColor || ''}
              handleColorChange={handleColorChange}
            />
          </Box>
          <Box {...CARD_STYLE}>
            <RowIndentation
              handleAlignemntChange={handleAlignemntChange}
              selectedAlignemnt={selectedRow?.formatting?.textAlign || ''}
            />
          </Box>
        </>
      )}
      {widget?.instrument_type === 'variance' && selectedRow && (
        <Box {...CARD_STYLE}>
          <RowSort
            rowSort={selectedRow?.formatting?.row_sort}
            hasDimension={Boolean(selectedRow?.dimension_id)}
            isPercentageIndicator={isPercentageIndicator}
            onRowSortChange={handleRowSortChange}
          />
        </Box>
      )}
    </Flex>
  );
};

export default Setup;
